"use client";

import { useRef, useState } from "react";
import { gsap, useGSAP } from "@/lib/gsap";

/**
 * TypeWriter — types out text character by character when scrolled into view.
 * Uses GSAP for timing. Shows a blinking cursor during and after typing.
 */
export default function TypeWriter({
  text,
  speed = 0.04,
  delay = 0,
  className = "",
  cursorColor = "#00D4FF",
}: {
  text: string;
  /** Seconds per character. Default: 0.04 */
  speed?: number;
  /** Delay before typing starts (seconds). Default: 0 */
  delay?: number;
  className?: string;
  cursorColor?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [displayed, setDisplayed] = useState("");
  const [typing, setTyping] = useState(false);
  const [done, setDone] = useState(false);

  useGSAP(
    () => {
      if (!ref.current) return;

      const mm = gsap.matchMedia();

      mm.add("(prefers-reduced-motion: no-preference)", () => {
        const proxy = { index: 0 };

        gsap.to(proxy, {
          index: text.length,
          duration: text.length * speed,
          delay,
          ease: "none",
          scrollTrigger: {
            trigger: ref.current,
            start: "top 85%",
            once: true,
          },
          onStart: () => setTyping(true),
          onUpdate: () => {
            setDisplayed(text.slice(0, Math.round(proxy.index)));
          },
          onComplete: () => {
            setTyping(false);
            setDone(true);
            setDisplayed(text);
          },
        });
      });

      mm.add("(prefers-reduced-motion: reduce)", () => {
        setDisplayed(text);
        setDone(true);
      });
    },
    { scope: ref },
  );

  return (
    <span ref={ref} className={className}>
      {displayed || "\u00A0"}
      {(typing || !done) && (
        <span
          className="inline-block w-[2px] h-[1em] ml-0.5 align-text-bottom"
          style={{
            background: cursorColor,
            animation: "blink 1.1s step-end infinite",
          }}
        />
      )}
    </span>
  );
}
